import React, { useState, useEffect, useCallback } from 'react';
import { functions, firestore } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot } from 'firebase/firestore';
import './TrueWarmup.css';

// Volume profiles the user can pick. Mirrors WARMUP_MODES in
// helpers/mailiveryWarmup.js. The per-day volume is adjustable via a slider.
const MODES = [
  {
    key: 'maintenance',
    label: 'Keep warm',
    defaultVolume: 15,
    replyRate: 35,
    desc: (v) => `Sends a steady ${v} warmup emails/day, forever, alongside your normal sending — keeps an established inbox in good standing.`,
  },
  {
    key: 'rampup',
    label: 'Ramp up',
    defaultVolume: 40,
    replyRate: 40,
    desc: (v) => `Starts low and automatically climbs up to ${v} warmup emails/day over the next days — best for a new or cold inbox.`,
  },
];
const VOLUME_MIN = 2;
const VOLUME_MAX = 50;
const REPLY_RATE_MIN = 0;
const REPLY_RATE_MAX = 100;
const DEFAULT_REPLY_RATE = 35;

// Mailivery health score: SPF/DMARC/MX validity ('valid' | 'invalid' | 'missing').
const HEALTH_CHECKS = [
  { key: 'spf', label: 'SPF', help: 'Proves your server is allowed to send for this domain.' },
  { key: 'dmarc', label: 'DMARC', help: 'Tells providers what to do with fake emails using your domain.' },
  { key: 'mx', label: 'MX', help: 'Confirms the domain can receive email.' },
];

function healthState(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'valid') return { cls: 'pass', icon: 'fa-check', word: 'OK' };
  if (v === 'invalid' || v === 'missing') return { cls: 'fail', icon: 'fa-times', word: 'Problem' };
  return { cls: 'unknown', icon: 'fa-minus', word: 'Unknown' };
}

function gradeColor(grade) {
  const g = String(grade || '').toUpperCase();
  if (g === 'A') return '#16a34a';
  if (g === 'B') return '#65a30d';
  if (g === 'C') return '#d97706';
  if (g) return '#dc2626';
  return '#9ca3af';
}

export default function TrueWarmup({ userId }) {
  const [accounts, setAccounts] = useState([]);
  const [statuses, setStatuses] = useState({}); // accountId → live status
  const [busy, setBusy] = useState({}); // accountId → 'enabling' | 'disabling' | false
  const [savingProfile, setSavingProfile] = useState({}); // accountId → bool (apply in flight)
  const [drafts, setDrafts] = useState({}); // accountId → { mode, volume, replyRate }
  const [replyRange, setReplyRange] = useState({ min: REPLY_RATE_MIN, max: REPLY_RATE_MAX });
  const [volRange, setVolRange] = useState({ min: VOLUME_MIN, max: VOLUME_MAX });
  const [advancedOpen, setAdvancedOpen] = useState({}); // accountId → bool
  const [loadingStatus, setLoadingStatus] = useState({});
  const [enableError, setEnableError] = useState({}); // accountId → string
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const ref = collection(firestore, 'world_users', userId, 'emailAccounts');
    const unsub = onSnapshot(ref, (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    });
    return () => unsub();
  }, [userId]);

  const fetchStatus = useCallback(async (accountId) => {
    setLoadingStatus((p) => ({ ...p, [accountId]: true }));
    try {
      const fn = httpsCallable(functions, 'mailiveryGetStatus');
      const res = await fn({ accountId, userId });
      setStatuses((p) => ({ ...p, [accountId]: res.data }));
    } catch (_) {
      // non-blocking
    } finally {
      setLoadingStatus((p) => ({ ...p, [accountId]: false }));
    }
  }, [userId]);

  // Load slider ranges once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'mailiveryGetConfig');
        const res = await fn({ userId });
        if (cancelled || !res.data) return;
        if (Number.isFinite(res.data.replyRateMax)) {
          setReplyRange({
            min: Number.isFinite(res.data.replyRateMin) ? res.data.replyRateMin : REPLY_RATE_MIN,
            max: res.data.replyRateMax,
          });
        }
        if (Number.isFinite(res.data.volumeMax)) {
          setVolRange({
            min: Number.isFinite(res.data.volumeMin) ? res.data.volumeMin : VOLUME_MIN,
            max: res.data.volumeMax,
          });
        }
      } catch (_) { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Pull live status for already-enrolled mailboxes once accounts load.
  useEffect(() => {
    accounts.forEach((a) => {
      if (a.mailiveryCampaignId && statuses[a.id] === undefined && !loadingStatus[a.id]) {
        fetchStatus(a.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const enable = useCallback(async (account) => {
    setBusy((p) => ({ ...p, [account.id]: 'enabling' }));
    setEnableError((p) => { const n = { ...p }; delete n[account.id]; return n; });
    try {
      const fn = httpsCallable(functions, 'mailiveryEnableWarmup');
      await fn({ accountId: account.id, userId });
      await fetchStatus(account.id);
    } catch (err) {
      console.error('Enable warmup failed:', err);
      setEnableError((p) => ({ ...p, [account.id]: err.message || 'Could not start warmup.' }));
    } finally {
      setBusy((p) => ({ ...p, [account.id]: false }));
    }
  }, [userId, fetchStatus]);

  const disable = useCallback(async (account) => {
    setBusy((p) => ({ ...p, [account.id]: 'disabling' }));
    setEnableError((p) => { const n = { ...p }; delete n[account.id]; return n; });
    try {
      const fn = httpsCallable(functions, 'mailiveryDisableWarmup');
      await fn({ accountId: account.id, userId });
    } catch (err) {
      console.error('Disable warmup failed:', err);
      setEnableError((p) => ({ ...p, [account.id]: err.message || 'Could not stop warmup.' }));
    } finally {
      setBusy((p) => ({ ...p, [account.id]: false }));
    }
  }, [userId]);

  // One-click migration off the legacy engine: tear down WarmUpInbox, then turn
  // on Mailivery (reuses an existing campaign for this mailbox if present).
  const migrate = useCallback(async (account) => {
    setBusy((p) => ({ ...p, [account.id]: 'migrating' }));
    setEnableError((p) => { const n = { ...p }; delete n[account.id]; return n; });
    try {
      const off = httpsCallable(functions, 'mailiveryDisableWarmup');
      await off({ accountId: account.id, userId, remove: true });
      const on = httpsCallable(functions, 'mailiveryEnableWarmup');
      await on({ accountId: account.id, userId });
      await fetchStatus(account.id);
    } catch (err) {
      console.error('Migrate to Mailivery failed:', err);
      setEnableError((p) => ({ ...p, [account.id]: err.message || 'Could not migrate warmup.' }));
    } finally {
      setBusy((p) => ({ ...p, [account.id]: false }));
    }
  }, [userId, fetchStatus]);

  // Saved (server-side) profile for an account, used as the draft baseline.
  const savedProfile = useCallback((account) => {
    const mode = account.mailiveryMode || 'maintenance';
    const def = MODES.find((m) => m.key === mode) || MODES[0];
    return {
      mode,
      volume: account.mailiveryVolume || def.defaultVolume,
      replyRate: Number.isFinite(account.mailiveryReplyRate) ? account.mailiveryReplyRate : (def.replyRate || DEFAULT_REPLY_RATE),
    };
  }, []);

  const getDraft = useCallback((account) => drafts[account.id] || savedProfile(account), [drafts, savedProfile]);

  const editDraft = useCallback((account, patch) => {
    setDrafts((p) => ({ ...p, [account.id]: { ...getDraft(account), ...patch } }));
  }, [getDraft]);

  const isDirty = useCallback((account) => {
    const d = drafts[account.id];
    if (!d) return false;
    const s = savedProfile(account);
    return d.mode !== s.mode || d.volume !== s.volume || d.replyRate !== s.replyRate;
  }, [drafts, savedProfile]);

  const saveProfile = useCallback(async (account) => {
    const d = drafts[account.id];
    if (!d) return;
    setSavingProfile((p) => ({ ...p, [account.id]: true }));
    try {
      const fn = httpsCallable(functions, 'mailiveryUpdateSettings');
      await fn({ accountId: account.id, userId, mode: d.mode, volume: d.volume, replyRate: d.replyRate });
      setDrafts((p) => { const n = { ...p }; delete n[account.id]; return n; }); // re-sync from Firestore
      await fetchStatus(account.id);
    } catch (err) {
      console.error('Apply warmup settings failed:', err);
    } finally {
      setSavingProfile((p) => ({ ...p, [account.id]: false }));
    }
  }, [drafts, userId, fetchStatus]);

  if (!loaded) {
    return <div className="tw-wrap"><div className="tw-loading">Loading mailboxes…</div></div>;
  }

  return (
    <div className="tw-wrap">
      <h3 className="tw-title">Warmup</h3>

      {accounts.length === 0 ? (
        <div className="tw-empty">
          <div className="tw-empty-icon">📭</div>
          <p>No email accounts yet. Add a mailbox in the Email Accounts tab first.</p>
        </div>
      ) : (
        <div className="tw-list">
          {accounts.map((account) => {
            const mailiveryStatus = String(account.mailiveryStatus || '').toLowerCase();
            const isMailivery = account.warmupProvider === 'mailivery'
              && !!account.mailiveryCampaignId
              && mailiveryStatus !== 'paused'
              && mailiveryStatus !== 'deleted';
            const isLegacy = account.warmupProvider === 'warmupinbox' && !!account.warmupInboxId;
            const isOn = isMailivery || isLegacy;
            const st = statuses[account.id] || {};
            const camp = st.campaign || null;
            const health = st.health || null;
            const metrics = st.metrics || null;
            const grade = health && health.grade ? health.grade : null;
            const draft = getDraft(account);
            const modeDef = MODES.find((m) => m.key === draft.mode) || MODES[0];
            const dirty = isDirty(account);
            const saving = savingProfile[account.id];
            const busyState = busy[account.id];
            const isBusy = !!busyState;
            const BUSY_LABEL = { enabling: 'Starting warmup…', disabling: 'Stopping…', migrating: 'Migrating to Mailivery…' };

            return (
              <div key={account.id} className={`tw-card ${isOn ? 'tw-card-on' : ''}`}>
                <div className="tw-card-head">
                  <div className="tw-card-id">
                    <div className="tw-card-name">{account.sender_name || account.email}</div>
                    <div className="tw-card-email">{account.email}</div>
                  </div>
                  <div className="tw-card-head-right">
                    {isBusy ? (
                      <span className="tw-busy">
                        <i className="fa fa-spinner fa-spin" />
                        {BUSY_LABEL[busyState] || 'Working…'}
                      </span>
                    ) : (
                      <span className={`tw-state ${isOn ? 'on' : 'off'}`}>
                        {isOn ? 'Warmup on' : 'Warmup off'}
                      </span>
                    )}
                    <button
                      className={`tw-toggle ${isOn ? 'on' : ''} ${isBusy ? 'busy' : ''}`}
                      disabled={isBusy}
                      onClick={() => (isLegacy ? migrate(account) : (isOn ? disable(account) : enable(account)))}
                      title={isLegacy ? 'Migrate this inbox to Mailivery' : (isOn ? 'Turn warmup off for this inbox' : 'Turn warmup on for this inbox')}
                    >
                      <span className="tw-toggle-knob">
                        {isBusy && <i className="fa fa-spinner fa-spin" />}
                      </span>
                    </button>
                  </div>
                </div>

                {enableError[account.id] && (
                  <div className="tw-error-hint">
                    <i className="fa fa-exclamation-triangle" />
                    <span>{enableError[account.id]}</span>
                  </div>
                )}

                {!isOn && !isBusy && (
                  <div className="tw-off-hint">
                    <i className="fa fa-shield-alt" />
                    <span>
                      Warmup is off for this inbox. Turn it on to protect its reputation so your emails
                      keep reaching the inbox.
                    </span>
                  </div>
                )}

                {isLegacy && !isBusy && (
                  <div className="tw-off-hint">
                    <i className="fa fa-sync" />
                    <span>
                      This inbox is still on the previous warmup engine. Click the switch to migrate
                      it to Mailivery — it stops the old engine and starts Mailivery automatically.
                    </span>
                  </div>
                )}

                {isMailivery && (
                  <>
                    {/* Warmup profile — edit locally, then push to Mailivery with one button */}
                    <div className="tw-cfg">
                      <div className="tw-cfg-row">
                        <span className="tw-modes-label">Warmup style</span>
                        <div className="tw-modes-opts">
                          {MODES.map((m) => (
                            <button
                              key={m.key}
                              className={`tw-mode ${draft.mode === m.key ? 'active' : ''}`}
                              disabled={saving}
                              onClick={() => editDraft(account, { mode: m.key })}
                            >
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="tw-vol">
                        <div className="tw-vol-top">
                          <span className="tw-vol-label">
                            {draft.mode === 'rampup' ? 'Target volume' : 'Emails per day'}
                          </span>
                          <span className="tw-vol-val">{draft.volume}<span className="tw-vol-unit">/day</span></span>
                        </div>
                        <input
                          type="range"
                          className="tw-vol-range"
                          min={volRange.min}
                          max={volRange.max}
                          value={draft.volume}
                          disabled={saving}
                          onChange={(e) => editDraft(account, { volume: Number(e.target.value) })}
                        />
                        <div className="tw-vol-scale"><span>{volRange.min}</span><span>{volRange.max}/day</span></div>
                      </div>

                      <div className="tw-adv">
                        <button
                          className="tw-adv-toggle"
                          onClick={() => setAdvancedOpen((p) => ({ ...p, [account.id]: !p[account.id] }))}
                        >
                          <i className={`fa fa-chevron-${advancedOpen[account.id] ? 'down' : 'right'}`} />
                          Advanced settings
                        </button>

                        {advancedOpen[account.id] && (
                          <div className="tw-adv-body">
                            <div className="tw-vol">
                              <div className="tw-vol-top">
                                <span className="tw-vol-label">Reply rate</span>
                                <span className="tw-vol-val">{draft.replyRate}<span className="tw-vol-unit">%</span></span>
                              </div>
                              <input
                                type="range"
                                className="tw-vol-range"
                                min={replyRange.min}
                                max={replyRange.max}
                                value={draft.replyRate}
                                disabled={saving}
                                onChange={(e) => editDraft(account, { replyRate: Number(e.target.value) })}
                              />
                              <div className="tw-vol-scale"><span>{replyRange.min}%</span><span>{replyRange.max}%</span></div>
                              <div className="tw-topics-hint">
                                How often warmup emails get a reply. Higher means a stronger
                                "real conversation" signal — around 35% is natural.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="tw-cfg-foot">
                        <span className="tw-modes-desc">{modeDef.desc(draft.volume)}</span>
                        <button
                          className={`tw-apply ${dirty ? 'active' : ''}`}
                          disabled={!dirty || saving}
                          onClick={() => saveProfile(account)}
                        >
                          {saving ? <><i className="fa fa-spinner fa-spin" /> Applying…</> : (dirty ? 'Apply changes' : 'Saved')}
                        </button>
                      </div>
                    </div>

                    {(grade || health) && (
                      <div className="tw-setup">
                        <div className="tw-setup-head">
                          <span className="tw-setup-title">Inbox health</span>
                          {grade && (
                            <span className="tw-setup-score" style={{ color: gradeColor(grade) }}>
                              {grade}<span className="tw-setup-score-max"> grade</span>
                            </span>
                          )}
                        </div>
                        {health && (
                          <div className="tw-checks">
                            {HEALTH_CHECKS.map((c) => {
                              const s = healthState(health[c.key]);
                              return (
                                <span
                                  key={c.key}
                                  className={`tw-check ${s.cls}`}
                                  title={`${c.label}: ${s.word}. ${c.help}`}
                                >
                                  <i className={`fa ${s.icon}`} />
                                  {c.label}
                                </span>
                              );
                            })}
                            {Number.isFinite(health.domain_age) && (
                              <span className="tw-check unknown" title="How long this domain has existed. Older domains are trusted more.">
                                <i className="fa fa-calendar" />
                                {health.domain_age}d old
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="tw-stats-head">
                      <span>Recent activity</span>
                      <button
                        className="tw-refresh"
                        disabled={loadingStatus[account.id]}
                        onClick={() => fetchStatus(account.id)}
                        title="Reload the latest numbers from the warmup network"
                      >
                        <i className={`fa fa-sync ${loadingStatus[account.id] ? 'fa-spin' : ''}`} />
                        {loadingStatus[account.id] ? 'Updating…' : 'Update'}
                      </button>
                    </div>
                    <div className="tw-stats">
                      <div className="tw-stat" title="Warmup emails this inbox sent into the network.">
                        <span className="tw-stat-val">{metrics ? metrics.sent : (camp ? camp.sent_in_last_45_days : '—')}</span>
                        <span className="tw-stat-lbl">Sent</span>
                      </div>
                      <div className="tw-stat" title="Warmup emails that got a reply — a strong 'this sender is real' signal to email providers.">
                        <span className="tw-stat-val">{metrics ? metrics.replies : '—'}</span>
                        <span className="tw-stat-lbl">Got replies</span>
                      </div>
                      <div className="tw-stat" title="Warmup emails that landed in spam. Lower is better — the warmup keeps rescuing these.">
                        <span className="tw-stat-val" style={{ color: metrics && metrics.spam > 0 ? '#d97706' : undefined }}>
                          {metrics ? metrics.spam : '—'}
                        </span>
                        <span className="tw-stat-lbl">Hit spam</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
